/**
 * Exchange Rate Service
 * 
 * Service for fetching and managing currency exchange rates from ExchangeRate-API
 * https://www.exchangerate-api.com/
 */

import { supabase } from '../supabase';
import { CURRENCIES } from '../constants/currencies';
import type { Currency } from '../../types/currency';

interface ExchangeRateAPIResponse {
  result: string;
  documentation: string;
  terms_of_use: string;
  time_last_update_unix: number;
  time_last_update_utc: string;
  time_next_update_unix: number;
  time_next_update_utc: string;
  base_code: string;
  conversion_rates: Record<string, number>;
}

class ExchangeRateService {
  private readonly BASE_URL = 'https://v6.exchangerate-api.com/v6';

  /**
   * Validate API Key by making a test request
   */
  async validateApiKey(apiKey: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.BASE_URL}/${apiKey}/latest/USD`);
      
      if (!response.ok) {
        return false;
      }

      const data: ExchangeRateAPIResponse = await response.json();
      return data.result === 'success';
    } catch (error) {
      console.error('[ExchangeRateService] Error validating API key:', error);
      return false;
    }
  }

  /**
   * Fetch exchange rates for a base currency
   */
  async fetchRates(apiKey: string, baseCurrency: Currency): Promise<Record<string, number> | null> {
    try {
      const response = await fetch(`${this.BASE_URL}/${apiKey}/latest/${baseCurrency}`);
      
      if (!response.ok) {
        console.error(`[ExchangeRateService] API error: ${response.status}`);
        return null;
      }

      const data: ExchangeRateAPIResponse = await response.json();
      
      if (data.result !== 'success') {
        console.error('[ExchangeRateService] API returned error:', data);
        return null;
      }

      return data.conversion_rates;
    } catch (error) {
      console.error('[ExchangeRateService] Error fetching rates:', error);
      return null;
    }
  }

  /**
   * Update all exchange rates for all supported currencies
   */
  async updateAllRates(userId: string, apiKey: string): Promise<void> {
    const supportedCurrencies = Object.keys(CURRENCIES) as Currency[];
    
    console.log('[ExchangeRateService] Updating rates for currencies:', supportedCurrencies);

    // Fetch rates for each base currency
    for (const baseCurrency of supportedCurrencies) {
      const rates = await this.fetchRates(apiKey, baseCurrency);
      
      if (!rates) {
        console.warn(`[ExchangeRateService] Failed to fetch rates for ${baseCurrency}`);
        continue;
      }

      // Save rates for this base currency
      for (const targetCurrency of supportedCurrencies) {
        if (baseCurrency === targetCurrency) continue; // Skip same currency
        
        const rate = rates[targetCurrency];
        
        if (!rate) {
          console.warn(`[ExchangeRateService] No rate found for ${baseCurrency} -> ${targetCurrency}`);
          continue;
        }

        // Upsert rate in database
        const { error } = await supabase
          .from('exchange_rates')
          .upsert({
            user_id: userId,
            base_currency: baseCurrency,
            target_currency: targetCurrency,
            rate: rate,
            source: 'exchangerate-api',
            updated_at: new Date().toISOString(),
          }, {
            onConflict: 'user_id,base_currency,target_currency'
          });

        if (error) {
          console.error(`[ExchangeRateService] Error saving rate ${baseCurrency}->${targetCurrency}:`, error);
        }
      }
    }

    console.log('[ExchangeRateService] All rates updated successfully');
  }

  /**
   * Get exchange rate from database (cached)
   */
  async getRate(userId: string, baseCurrency: Currency, targetCurrency: Currency): Promise<number | null> {
    if (baseCurrency === targetCurrency) {
      return 1; // Same currency = 1:1
    }

    try {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('rate')
        .eq('user_id', userId)
        .eq('base_currency', baseCurrency)
        .eq('target_currency', targetCurrency)
        .single();

      if (error || !data) {
        console.warn(`[ExchangeRateService] No rate found for ${baseCurrency}->${targetCurrency}`);
        return null;
      }

      return Number(data.rate);
    } catch (error) {
      console.error('[ExchangeRateService] Error getting rate:', error);
      return null;
    }
  }

  /**
   * Convert amount from one currency to another
   */
  async convert(
    userId: string,
    amount: number,
    fromCurrency: Currency,
    toCurrency: Currency
  ): Promise<number | null> {
    const rate = await this.getRate(userId, fromCurrency, toCurrency);
    
    if (rate === null) {
      return null;
    }

    return amount * rate;
  }

  /**
   * Get all rates for a user filtered by base currency
   */
  async getRatesForBase(userId: string, baseCurrency: Currency): Promise<Array<{
    target_currency: Currency;
    rate: number;
    updated_at: string;
  }>> {
    try {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('target_currency, rate, updated_at')
        .eq('user_id', userId)
        .eq('base_currency', baseCurrency)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('[ExchangeRateService] Error getting rates for base:', error);
        return [];
      }

      return (data || []) as Array<{
        target_currency: Currency;
        rate: number;
        updated_at: string;
      }>;
    } catch (error) {
      console.error('[ExchangeRateService] Exception getting rates:', error);
      return [];
    }
  }

  /**
   * Get all rates for a user (for display purposes)
   */
  async getAllRates(userId: string): Promise<Array<{
    base_currency: Currency;
    target_currency: Currency;
    rate: number;
    updated_at: string;
  }>> {
    try {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('base_currency, target_currency, rate, updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('[ExchangeRateService] Error getting all rates:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('[ExchangeRateService] Error getting all rates:', error);
      return [];
    }
  }

  /**
   * Check if rates need update (older than 24 hours)
   */
  async needsUpdate(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) {
        return true; // No rates = needs update
      }

      const lastUpdate = new Date(data.updated_at);
      const hoursSinceUpdate = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60);

      return hoursSinceUpdate >= 24; // Update if older than 24 hours
    } catch (error) {
      console.error('[ExchangeRateService] Error checking update status:', error);
      return true;
    }
  }
}

export const exchangeRateService = new ExchangeRateService();

